import '.a{display:flex;}.ssr-plugin'
import '.df{justify-content:flex-start;}.ssr-plugin'
import '.by{position:relative;}.ssr-plugin'
import '.bu{flex-shrink:0;}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
import '.bb{gap:0.5rem;}.ssr-plugin'
import '.dv{padding:0.375rem 0.75rem;}.ssr-plugin'
import '.dw{border-radius:0.5rem;}.ssr-plugin'
import '.dx{background:var(--green-50);}.ssr-plugin'
import '.dy{border:1px solid var(--green-200);}.ssr-plugin'
import '.g{justify-content:center;}.ssr-plugin'
import '.c6{width:1.5rem;}.ssr-plugin'
import '.c7{height:1.5rem;}.ssr-plugin'
import '.dq{border-radius:50%;}.ssr-plugin'
import '.dz{border:1px solid var(--gray-300);}.ssr-plugin'
import '.e0{background:var(--white);}.ssr-plugin'
import '.ba{cursor:pointer;}.ssr-plugin'
import '.w{position:absolute;}.ssr-plugin'
import '.bk{top:0px;}.ssr-plugin'
import '.bp{left:0px;}.ssr-plugin'
import '.t{width:100%;}.ssr-plugin'
import '.br{height:100%;}.ssr-plugin'
import '.e1{opacity:0;}.ssr-plugin'
import { useCallback, useEffect, useState } from 'react'
import { useQuery, useClient } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { CardWrapper } from '@app/components/blocks/card/CardWrapper'
import { Checkbox } from '@app/components/core/checkbox/Checkbox'
import { styled } from 'inlines'
import useMediaQuery from '@app/util/useMediaQuery'
import { LoaderWrapper } from '@app/components/blocks/loader/LoaderWrapper'
import { useToast } from '@app/util/hooks/useToast'
import { faUpload } from '@fortawesome/pro-regular-svg-icons'
import { faXmark, faFile } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FileSelector } from '@app/components/core/input/text/FileSelector'
import type { ViewPlotRegistryModel } from 'functions/app/plot-registry/view'
import type { ViewPlotRegistryApplicationModel } from 'functions/app/plot-registry/view-application'
import { CtaCard } from '@app/components/blocks/card/CtaCard'
import coupleHoldingOctagon from '@app/assets/images/couple_holding_octagon.svg'
import { useTranslations } from '@i18n/useLanguage' 
import { Alert } from '@app/components/core/alert/Alert'

type UploadDocumentsStepProps = {
  applicationId: number
  plotRegistryId: number
  municipalityName: string
  consentGiven: boolean
  onConsentChange: (consent: boolean) => void
  onAllDocumentsUploadedChange: (allUploaded: boolean) => void
}

export const UploadDocumentsStep = ({
  applicationId,
  plotRegistryId,
  municipalityName,
  consentGiven,
  onConsentChange,
  onAllDocumentsUploadedChange,
}: UploadDocumentsStepProps) => {
  const isMobile = useMediaQuery()
  const client = useClient()
  const { showSuccessToast, showFailToast } = useToast()

  const t = useTranslations().app.plotRegistry.uploadDocuments
  const { data: rawPlotRegistry } = useQuery(
    'view-plot-registry',
    { id: plotRegistryId },
  )
  const plotRegistry = rawPlotRegistry as ViewPlotRegistryModel | undefined

  const { data: rawApplication } = useQuery(
    'view-plot-registry-application',
    { applicationId },
  )
  const application = rawApplication as ViewPlotRegistryApplicationModel | undefined

  // Local tracking of successful uploads as fallback when the backend
  // file record linking fails (known db.create issue in upload-file).
  const [localUploads, setLocalUploads] = useState<
    Map<number, { name: string; docId: number }>
  >(new Map())

  const handleFileUpload = useCallback(
    async (requiredDocumentId: number, files: File[]) => {
      if (files.length === 0) { return }

      const file = files[0]
      try {
        // First create/get the application document record
        const docId = (await client.call('upload-plot-registry-document', {
          applicationId,
          requiredDocumentId,
        })) as number

        // Then upload the file and link it to the document record
        await client.stream('upload-file', {
          mimeType: file.type,
          contents: file,
          size: file.size,
          fileName: `plot-registry-doc-${Date.now()}-${file.name}`,
          payload: { plotRegistryApplicationDoc: { id: docId } },
        })

        // Track locally so the UI reflects the upload even if the backend
        // file record creation silently fails
        setLocalUploads((prev) => {
          const next = new Map(prev)
          next.set(requiredDocumentId, { name: file.name, docId })
          return next
        })

        showSuccessToast(t.uploadSuccess, 1.5)
      } catch {
        showFailToast()
      }
    },
    [applicationId, client, showSuccessToast, showFailToast, t.uploadSuccess],
  )

  const handleDeleteDocument = useCallback(
    async (documentId: number, requiredDocumentId: number) => {
      try {
        await client.call('delete-plot-registry-document', { documentId })
        setLocalUploads((prev) => {
          const next = new Map(prev)
          next.delete(requiredDocumentId)
          return next
        })
        showSuccessToast(t.deleteSuccess, 1.5)
      } catch {
        showFailToast()
      }
    },
    [client, showSuccessToast, showFailToast, t.deleteSuccess],
  )

  const getUploadedDoc = useCallback(
    (requiredDocumentId: number) => {
      return (application?.documents ?? []).find(
        (d: any) => d.requiredDocument?.id === requiredDocumentId,
      )
    },
    [application],
  )

  useEffect(() => {
    if (!plotRegistry || !application) {
      onAllDocumentsUploadedChange(false)
      return
    }
    const requiredDocs = plotRegistry.requiredDocuments ?? []
    const allUploaded =
      requiredDocs.length > 0 &&
      requiredDocs.every((reqDoc) => {
        const uploaded = getUploadedDoc(reqDoc.id)
        return uploaded?.file || localUploads.has(reqDoc.id)
      })
    onAllDocumentsUploadedChange(allUploaded)
  }, [plotRegistry, application, onAllDocumentsUploadedChange, getUploadedDoc, localUploads])

  if (!plotRegistry || !application) {
    return <LoaderWrapper loading={true}>{null}</LoaderWrapper>
  }

  return (
    <Row
      gap="3rem"
      style={{
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        minHeight: '100vh',
        justifyContent: 'center',
      }}
    >
      <Column
        gap="0.5rem"
        style={{
          flex: '1 1 0',
          minWidth: 0,
          width: isMobile ? '100%' : '50%',
          maxWidth: '100%',
        }}
      >
        <div className="a df">
        <CtaCard
          title=''
          illustration={coupleHoldingOctagon}
        />
        </div>
        <Text.Small
          style={{
            color: 'var(--shades-red-400)',
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          {t.stepLabel}
        </Text.Small>
        <Title.H3>{t.title}</Title.H3>
        <Text.Medium style={{ color: 'var(--gray-500)' }}>
          {t.description.replace('{municipalityName}', municipalityName)}
        </Text.Medium>

        <Alert  
          size="medium"
          variant="warning"
        >
          <strong>{t.warningBold}</strong> {t.warningText}
        </Alert> 
      </Column>

      <Column
        gap="0.75rem"
        style={{
          flex: '1 1 0',
          minWidth: 0,
          width: isMobile ? '100%' : '50%',
          maxWidth: '100%',
        }}
      >
        {(plotRegistry.requiredDocuments ?? []).map((reqDoc) => {
          const uploadedDoc = getUploadedDoc(reqDoc.id)
          const localUpload = localUploads.get(reqDoc.id)
          const hasUpload = !!(uploadedDoc?.file || localUpload)
          const displayName =
            uploadedDoc?.file?.name || localUpload?.name || ''
          const deleteDocId = uploadedDoc?.id ?? localUpload?.docId
          return (
            <CardWrapper
              key={reqDoc.id}
              style={{
                border: hasUpload
                  ? '1px solid var(--primary-blue)'
                  : 'var(--gray-border)',
                  width: '100%'
              }}
            >
              <Row
                style={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Column gap="0.25rem" style={{ flex: 1 }}>
                  <Text.Medium isBold>{reqDoc.title}</Text.Medium>
                  <Text.Small style={{ color: 'var(--gray-500)' }}>
                    {reqDoc.description}
                  </Text.Small>
                </Column>

                <div className="by bu">
                  {hasUpload ? (
                    <Row gap="0.5rem" style={{ alignItems: 'center' }}>
                      <div className="a c bb dv dw dx dy"
                      >
                        <FontAwesomeIcon
                          icon={faFile}
                          style={{ color: 'var(--green-600)', fontSize: '0.875rem' }}
                        />
                        <Text.ExtraSmall
                          style={{
                            color: 'var(--green-700)',
                            maxWidth: '10rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayName}
                        </Text.ExtraSmall>
                      </div>
                      {deleteDocId !== undefined && (
                        <styled.button className="a c g c6 c7 dq dz e0 ba bu"
                          onClick={() =>
                            void handleDeleteDocument(deleteDocId, reqDoc.id)
                          }
                          style={{
                            '&:hover': {
                              background: 'var(--red-50)',
                              borderColor: 'var(--red-300)',
                            },
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faXmark}
                            style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}
                          />
                        </styled.button>
                      )}
                    </Row>
                  ) : (
                    <>
                      <Button
                        label={t.uploadButton}
                        icon={faUpload}
                        iconPosition="left"
                        variant="secondary"
                        size="small"
                      />
                      <div className="w bk bp t br e1 ba"
                      >
                        <FileSelector
                          maxFiles={1}
                          maxSizeMB={25}
                          allowedFileTypes={[
                            'application/pdf',
                            'image/jpeg',
                            'image/png',
                          ]}
                          containerStyle={{
                            width: '100%',
                            height: '100%',
                          }}
                          onFileSelectionUpdated={(files) =>
                            void handleFileUpload(reqDoc.id, files)
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              </Row>
            </CardWrapper>
          )
        })}

        <Row gap="0.5rem" style={{ alignItems: 'center', marginTop: '1rem' }}>
          <Checkbox
            selected={consentGiven}
            onClick={() => onConsentChange(!consentGiven)}
          />
          <Text.Small>
            {t.consent.replace('{municipalityName}', municipalityName)}
          </Text.Small>
        </Row>
      </Column>
    </Row>
  )
}
